import { useEffect, useRef } from "react";

export type DialerHotkeyHandlers = {
  onOutcome(n: number): void;
  onSkip(): void;
  onNote(): void;
  onEnter(): void;
  onSpace(): void;
  onEscape(): void;
  onSaveShortcut(): void;
  onHelp(): void;
  /** GAVION X2: O focuses the objections search. Optional: the key passes through without it. */
  onObjections?(): void;
};

const isTyping = (t: EventTarget | null): boolean => {
  if (!(t instanceof HTMLElement)) return false;
  return (
    t.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName)
  );
};

const INTERACTIVE_ROLES = /^(button|link|menuitem|checkbox|switch|tab)$/;
const isInteractive = (t: EventTarget | null): boolean => {
  if (!(t instanceof HTMLElement)) return false;
  return (
    ["BUTTON", "A"].includes(t.tagName) ||
    INTERACTIVE_ROLES.test(t.getAttribute("role") ?? "")
  );
};

/** True when Escape belongs to another dialog/popper (not the dialer's own panel). */
const inForeignOverlay = (t: EventTarget | null): boolean => {
  if (!(t instanceof Element)) return false;
  const o = t.closest('[role="dialog"],[data-radix-popper-content-wrapper]');
  return o !== null && o.closest("[data-dialer-panel]") === null;
};

/** Pure key handler (spec 04 §4.3 Keyboard). Returns true when the key was consumed. */
export function handleDialerKey(
  e: Pick<KeyboardEvent, "key" | "ctrlKey" | "metaKey" | "altKey" | "target"> &
    Partial<Pick<KeyboardEvent, "repeat" | "isComposing" | "defaultPrevented">>,
  h: DialerHotkeyHandlers,
): boolean {
  if (e.isComposing || e.defaultPrevented) return false;
  if (e.key === "Escape") {
    if (inForeignOverlay(e.target)) return false;
    h.onEscape();
    return true;
  }
  if (e.repeat) return false;
  if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
    h.onSaveShortcut();
    return true;
  }
  if (isTyping(e.target) || e.ctrlKey || e.metaKey || e.altKey) return false;
  if (/^[1-8]$/.test(e.key)) {
    h.onOutcome(Number(e.key));
    return true;
  }
  switch (e.key) {
    case "s":
    case "S":
      h.onSkip();
      return true;
    case "n":
    case "N":
      h.onNote();
      return true;
    case "Enter":
      if (isInteractive(e.target)) return false;
      h.onEnter();
      return true;
    case " ":
      if (isInteractive(e.target)) return false;
      h.onSpace();
      return true;
    case "?":
      h.onHelp();
      return true;
    case "o":
    case "O":
      if (!h.onObjections) return false;
      h.onObjections();
      return true;
    default:
      return false;
  }
}

export function useDialerHotkeys(
  handlers: DialerHotkeyHandlers,
  enabled: boolean,
): void {
  const ref = useRef(handlers);
  useEffect(() => {
    ref.current = handlers;
  }, [handlers]);
  useEffect(() => {
    if (!enabled) return;
    const listener = (e: KeyboardEvent) => {
      if (handleDialerKey(e, ref.current)) e.preventDefault();
    };
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  }, [enabled]);
}
